import { useEffect } from 'react';
import { Locale } from '../types';
import { translate } from './translations';

const LOCALIZED_ATTRIBUTES = ['aria-label', 'placeholder', 'title', 'alt'] as const;
type LocalizedText = Text & { __mapblOriginalText?: string; __mapblLocalizedText?: string };
type LocalizedElement = Element & {
  __mapblOriginalAttributes?: Record<string, string>;
  __mapblLocalizedAttributes?: Record<string, string>;
};

const translateTree = (root: Node, locale: Locale) => {
  const parentElement = root.nodeType === Node.ELEMENT_NODE
    ? root as Element
    : root.parentElement;
  if (parentElement?.closest('[data-no-i18n]')) return;

  if (root.nodeType === Node.TEXT_NODE) {
    const node = root as LocalizedText;
    const raw = node.textContent || '';
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (!node.__mapblOriginalText || (node.__mapblLocalizedText && trimmed !== node.__mapblLocalizedText)) {
      node.__mapblOriginalText = trimmed;
    }
    const localized = translate(node.__mapblOriginalText, locale);
    node.__mapblLocalizedText = localized;
    if (localized !== trimmed) node.textContent = raw.replace(trimmed, localized);
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
  if (root.nodeType === Node.ELEMENT_NODE) {
    const element = root as LocalizedElement;
    element.__mapblOriginalAttributes ??= {};
    element.__mapblLocalizedAttributes ??= {};
    for (const attribute of LOCALIZED_ATTRIBUTES) {
      const value = element.getAttribute(attribute);
      if (!value) continue;
      if (!element.__mapblOriginalAttributes[attribute] || (element.__mapblLocalizedAttributes[attribute] && value !== element.__mapblLocalizedAttributes[attribute])) {
        element.__mapblOriginalAttributes[attribute] = value;
      }
      const localized = translate(element.__mapblOriginalAttributes[attribute], locale);
      element.__mapblLocalizedAttributes[attribute] = localized;
      if (localized !== value) element.setAttribute(attribute, localized);
    }
  }
  root.childNodes.forEach((child) => translateTree(child, locale));
};

const restoreTree = (root: Node) => {
  if (root.nodeType === Node.TEXT_NODE) {
    const node = root as LocalizedText;
    if (node.__mapblOriginalText) node.textContent = node.__mapblOriginalText;
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
  if (root.nodeType === Node.ELEMENT_NODE) {
    const element = root as LocalizedElement;
    Object.entries(element.__mapblOriginalAttributes || {}).forEach(([name, value]) => element.setAttribute(name, value));
  }
  root.childNodes.forEach(restoreTree);
};

export const useLocalizedDocument = (locale: Locale) => {
  useEffect(() => {
    document.documentElement.lang = locale;
    translateTree(document.body, locale);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') translateTree(mutation.target, locale);
        if (mutation.type === 'attributes') translateTree(mutation.target, locale);
        mutation.addedNodes.forEach((node) => translateTree(node, locale));
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: [...LOCALIZED_ATTRIBUTES] });
    return () => {
      observer.disconnect();
      restoreTree(document.body);
    };
  }, [locale]);
};
